import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { toast } from "sonner";
import { generateOverviewPdf } from "@/lib/overviewPdf";

const links = [
  { label: "Approach", href: "#approach" },
  { label: "Framework", href: "#framework" },
  { label: "Modules", href: "#modules" },
  { label: "Regions", href: "#regions" },
  { label: "Outcomes", href: "#outcomes" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.2, 0.7, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ink/70 border-b border-[var(--line)]"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="h-8 w-8 border border-gold/60 grid place-items-center">
            <span className="font-display text-gold text-xl leading-none">A</span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-display text-lg text-ivory tracking-wide">
              Advik Ventures
            </span>
            <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
              Capability, Engineered
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-ivory/80 hover:text-ivory gold-underline transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              try {
                generateOverviewPdf();
                toast.success("Overview PDF downloaded.");
              } catch (e) {
                console.error(e);
                toast.error("Could not generate overview. Please try again.");
              }
            }}
            data-testid="nav-download-overview"
            className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-xs tracking-eyebrow uppercase text-ivory/80 hover:text-gold transition-colors border border-transparent hover:border-[var(--line-strong)]"
          >
            <Download size={13} strokeWidth={1.5} />
            Overview PDF
          </button>
          <a
            href="#workshop"
            data-testid="nav-cta-workshop"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-xs tracking-eyebrow uppercase border border-gold/60 text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            Schedule Workshop
            <span aria-hidden>→</span>
          </a>
          <button
            className="md:hidden text-ivory p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-[var(--line)] bg-ink/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-ivory/80 hover:text-gold text-lg font-display"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#workshop"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-workshop-mobile"
                className="mt-2 inline-flex items-center gap-2 px-4 py-3 text-xs tracking-eyebrow uppercase border border-gold/60 text-gold w-fit"
              >
                Schedule Workshop →
              </a>
              <button
                type="button"
                onClick={() => {
                  try {
                    generateOverviewPdf();
                    toast.success("Overview PDF downloaded.");
                    setOpen(false);
                  } catch (e) {
                    toast.error("Could not generate overview.");
                  }
                }}
                data-testid="nav-download-overview-mobile"
                className="mt-1 inline-flex items-center gap-2 px-4 py-3 text-xs tracking-eyebrow uppercase text-ivory/80 w-fit"
              >
                <Download size={13} strokeWidth={1.5} />
                Download Overview PDF
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
